// Shared JSON schema for the Form Designer document tree.
// schemaVersion is bumped whenever this shape changes, so saved templates can be migrated.

export type ElementType =
  | 'text' | 'label' | 'paragraph' | 'heading'
  | 'image'
  | 'table' | 'hline' | 'vline' | 'rect'
  | 'checkbox' | 'radio' | 'dropdown' | 'textbox' | 'textarea'
  | 'date' | 'signature' | 'pagenumber';

export interface TextRun {
  type: 'text';
  value: string;
}

export interface VariableRun {
  type: 'variable';
  fieldKey: string;
  sectionKey?: string;
}

export interface InputRun {
  type: 'textbox' | 'textarea' | 'checkbox' | 'radio';
  value?: string;
  fieldKey?: string;
  sectionKey?: string;
  checked?: boolean;
}

export type ContentRun = TextRun | VariableRun | InputRun;

export interface ElementStyle {
  fontFamily?: string;
  fontSize?: number;
  fontWeight?: 'normal' | 'bold';
  fontStyle?: 'normal' | 'italic';
  textDecoration?: 'none' | 'underline';
  color?: string;
  textAlign?: 'left' | 'center' | 'right' | 'justify';
  background?: string;
  borderWidth?: number;
  borderStyle?: 'none' | 'solid' | 'dashed' | 'dotted';
  borderColor?: string;
  borderRadius?: number;
  padding?: number;
  lineHeight?: number;
  letterSpacing?: string;
}

export interface TableMerge {
  r: number;
  c: number;
  rowSpan: number;
  colSpan: number;
}

export interface TableProps {
  rows: number;
  cols: number;
  colWidths: number[]; // percentages, sum to 100
  rowHeights: number[]; // px
  cells: ContentRun[][][]; // [row][col] -> content runs
  merges: TableMerge[];
}

export interface TypeProps {
  table?: TableProps;
  checkbox?: { label: string; checked: boolean; groupName?: string };
  radio?: { label: string; checked: boolean; groupName: string };
  dropdown?: { options: string[] };
  image?: { src?: string; fit?: 'contain' | 'cover' | 'fill' };
  signature?: { placeholderText: string };
}

export interface FieldBinding {
  dynamicField?: string;
  sectionKey?: string;
  // More master fields mapped to the same input; dynamicField stays the first one so single-field readers keep working
  extraFields?: { fieldKey: string; sectionKey?: string }[];
  defaultValue?: string;
  required?: boolean;
  readOnly?: boolean;
}

export interface FormElement {
  id: string;
  type: ElementType;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  zIndex: number;
  locked: boolean;
  visible: boolean;
  groupId?: string | null;
  style: ElementStyle;
  content?: ContentRun[];
  bindings?: FieldBinding;
  typeProps?: TypeProps;
}

export interface FormPage {
  id: string;
  elements: FormElement[];
}

export interface FormMargins {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface FormDocument {
  id: string | null;
  templateId: string | null;
  name: string;
  schemaVersion: 1;
  pageSize: 'A4' | 'Letter';
  orientation: 'portrait' | 'landscape';
  margins: FormMargins;
  pages: FormPage[];
}

export const PAGE_SIZE_PX: Record<FormDocument['pageSize'], { width: number; height: number }> = {
  A4: { width: 794, height: 1123 }, // 96dpi @ 210x297mm
  Letter: { width: 816, height: 1056 }, // 96dpi @ 8.5x11in
};
